<template>
    <div class="max-w-2xl mx-auto space-y-6">
        <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
                <UButton to="/payments" color="gray" variant="ghost" icon="i-ph-arrow-left-bold" />
                <h1 class="text-2xl font-semibold text-gray-700">PO #{{ order?.invoice_number }}</h1>
            </div>
            <UBadge v-if="order" :color="order.approved_at ? 'green' : 'orange'" :label="order.approved_at ? 'Approved' : 'Pending'" variant="subtle" size="lg" />
        </div>

        <div v-if="loading" class="flex items-center justify-center py-12">
            <span class="text-sm text-gray-400">Loading...</span>
        </div>

        <template v-else-if="order">
            <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                <div class="px-6 py-5 border-b border-gray-100">
                    <div class="flex items-center justify-between">
                        <div>
                            <p class="text-xs text-gray-400 uppercase tracking-wide">SAMU Liban SAL</p>
                            <p class="text-xs text-gray-400">Adib Ishac Street, Horizon Building</p>
                            <p class="text-xs text-gray-400">Ashrafieh, Beirut, Lebanon</p>
                            <p class="text-xs text-gray-400">VAT ID: 3467677-601</p>
                        </div>
                        <div class="text-right">
                            <p class="text-sm font-semibold text-gray-700">PO #{{ order.invoice_number }}</p>
                            <p class="text-xs text-gray-400">{{ dayjs(order.created_at).format('MMMM D, YYYY') }}</p>
                            <p v-if="order.approved_at" class="text-xs text-gray-400">Paid {{ dayjs(order.approved_at).format('MMMM D, YYYY') }}</p>
                        </div>
                    </div>
                </div>

                <div class="px-6 py-4 border-b border-gray-100">
                    <p class="text-xs text-gray-400 uppercase tracking-wide mb-2">Customer</p>
                    <div v-if="order.customer" class="flex items-center space-x-3">
                        <Avatar size="sm" :path="order.customer.avatar" :placeholder="[order.customer.first_name, order.customer.last_name].filter(Boolean).join(' ')" />
                        <div>
                            <p class="text-sm font-medium">{{ [order.customer.first_name, order.customer.middle_name, order.customer.last_name].filter(Boolean).join(' ') }}</p>
                            <p class="text-xs text-gray-500">{{ order.email }}</p>
                            <PhoneLabel v-if="order.customer.phone" :value="order.customer.phone" class="text-xs text-gray-500" />
                        </div>
                    </div>
                    <p v-else class="text-sm text-gray-600">{{ order.email }}</p>

                    <div v-if="billingAddressLines.length" class="mt-3 pt-3 border-t border-gray-100">
                        <p class="text-xs text-gray-400 uppercase tracking-wide mb-1">Billing Address</p>
                        <p v-for="line in billingAddressLines" :key="line" class="text-sm text-gray-700 leading-5">{{ line }}</p>
                    </div>
                </div>

                <div class="px-6 py-4 border-b border-gray-100">
                    <p class="text-xs text-gray-400 uppercase tracking-wide mb-2">Product</p>
                    <p class="text-sm font-medium">{{ order.product?.name }}</p>
                    <p v-if="order.product?.description" class="text-xs text-gray-500">{{ order.product.description }}</p>
                </div>

                <div v-if="members.length" class="px-6 py-4 border-b border-gray-100">
                    <p class="text-xs text-gray-400 uppercase tracking-wide mb-2">Members ({{ members.length }})</p>
                    <ul class="space-y-1">
                        <li v-for="member in members" :key="member.id" class="flex items-center space-x-2 text-sm">
                            <UIcon name="i-ph-check-circle-fill" class="size-4 text-green-500 shrink-0" />
                            <span>{{ [member.first_name, member.last_name].filter(Boolean).join(' ') }}</span>
                        </li>
                    </ul>
                </div>

                <div class="px-6 py-4">
                    <p class="text-xs text-gray-400 uppercase tracking-wide mb-3">Amount</p>
                    <div class="space-y-2 text-sm">
                        <div class="flex justify-between font-semibold text-base">
                            <span>Total (VAT inclusive)</span>
                            <span class="font-mono">${{ (((order.total_price || 0) + (order.tax_amount || 0)) / 100).toFixed(2) }}</span>
                        </div>
                        <div class="flex justify-between text-gray-600">
                            <span>{{ order.quantity }} member(s) × ${{ ((order.unit_price || 0) / 100).toFixed(2) }}</span>
                        </div>
                        <div class="border-t border-gray-200 pt-2 space-y-1">
                            <div class="flex justify-between text-gray-500">
                                <span>Excl. VAT</span>
                                <span class="font-mono">${{ ((order.total_price || 0) / 100).toFixed(2) }}</span>
                            </div>
                            <div class="flex justify-between text-gray-500">
                                <span>VAT (11%)</span>
                                <span class="font-mono">${{ ((order.tax_amount || 0) / 100).toFixed(2) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm px-6 py-4 space-y-3">
                <p class="text-xs text-gray-400 uppercase tracking-wide">Timeline</p>
                <div class="space-y-2 text-sm">
                    <div class="flex items-center space-x-3">
                        <UIcon name="i-ph-clock-duotone" class="size-4 text-gray-400 shrink-0" />
                        <span>Created on {{ dayjs(order.created_at).format('lll') }}</span>
                    </div>
                    <div v-if="order.approved_at" class="flex items-center space-x-3">
                        <UIcon name="i-ph-check-circle-duotone" class="size-4 text-green-500 shrink-0" />
                        <span>Approved on {{ dayjs(order.approved_at).format('lll') }}</span>
                    </div>
                    <div v-else class="flex items-center space-x-3">
                        <UIcon name="i-ph-hourglass-medium-duotone" class="size-4 text-orange-400 shrink-0" />
                        <span class="text-orange-600">Awaiting payment</span>
                    </div>
                </div>
            </div>

            <div v-if="!order.approved_at" class="bg-orange-50 ring-1 ring-orange-200 rounded-lg px-6 py-4 space-y-3">
                <p class="text-sm text-orange-800 font-medium">Manual Approval</p>
                <p class="text-sm text-orange-700">If the payment was completed but the webhook was not received, you can manually approve this order. This will create subscriptions for all selected members.</p>
                <UButton @click="approveOrder" color="orange" size="sm" :loading="approving" icon="i-ph-check-circle-bold">
                    Approve & Activate Memberships
                </UButton>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat.js'
import { UserRole } from '~/types/user'

dayjs.extend(localizedFormat)

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })

const route = useRoute()
const client = useSupabaseClient()

const toast = useToast()

const loading = ref(false)
const approving = ref(false)
const order = ref<any>(null)
const members = ref<any[]>([])

useHead({ title: computed(() => order.value ? `PO #${order.value.invoice_number}` : 'Payment') })

const billingAddressLines = computed<string[]>(() => {
    const a = order.value?.billing_address
    if (!a) return []
    return [
        a.street,
        a.street2,
        [a.city, a.stateProvince, a.postcodeZip].filter(Boolean).join(', '),
        a.country,
    ].filter((line) => line && String(line).trim().length)
})

onMounted(() => fetchOrder())

async function fetchOrder() {
    loading.value = true

    const { data } = await client
        .from('purchase_orders')
        .select(`
            id, invoice_number, email, quantity, unit_price, total_price, tax_amount,
            member_customer_ids, approved_at, created_at,
            product:products (id, name, description),
            billing_address,
            customer:customers (id, first_name, middle_name, last_name, avatar, email, phone)
        `)
        .eq('id', route.params.id)
        .single()

    if (data) {
        order.value = data

        if (data.member_customer_ids?.length) {
            const { data: memberData } = await client
                .from('customers')
                .select('id, first_name, last_name')
                .in('id', data.member_customer_ids)

            if (memberData) members.value = memberData
        }
    }

    loading.value = false
}

async function approveOrder() {
    approving.value = true

    try {
        const { membersActivated } = await $fetch(`/api/payments/${route.params.id}/approve`, {
            method: 'POST',
        })

        toast.add({
            title: `Memberships activated for ${membersActivated?.length || 0} member(s).`,
            color: 'green',
            icon: 'i-ph-check-circle-duotone',
        })

        await fetchOrder()
    } catch (e: any) {
        toast.add({
            title: e?.data?.message || 'Failed to approve order.',
            color: 'red',
            icon: 'i-ph-warning-duotone',
        })
    }

    approving.value = false
}
</script>
